import React, { useState } from 'react';
import { Layout } from '../components/Layout';
import { SEO } from '../components/SEO';
import { Sun, Moon } from 'lucide-react';

export default function PitchDeck() {
  const [darkMode, setDarkMode] = useState(false);

  const bgClass = darkMode ? 'bg-slate-900' : 'bg-white';
  const textClass = darkMode ? 'text-slate-100' : 'text-gray-900';
  const secondaryTextClass = darkMode ? 'text-slate-300' : 'text-gray-700';
  const tertiaryTextClass = darkMode ? 'text-slate-400' : 'text-gray-600';
  const cardBgClass = darkMode ? 'bg-slate-800' : 'bg-gray-50';
  const borderClass = darkMode ? 'border-slate-700' : 'border-gray-200';
  const accentBgClass = darkMode ? 'bg-slate-700' : 'bg-blue-50';

  return (
    <Layout>
      <SEO
        title="Investor Pitch Deck - b2bcsv.com"
        description="b2bcsv.com seed round presentation - AI-powered lead generation for modern businesses"
        canonical="/pitch-deck"
      />

      <div className={`min-h-screen ${bgClass} ${textClass} transition-colors duration-300`}>
        {/* Theme Toggle - Fixed Position */}
        <div className="fixed top-24 right-6 z-50">
          <button
            onClick={() => setDarkMode(!darkMode)}
            className={`p-3 rounded-full shadow-lg ${cardBgClass} ${borderClass} border hover:scale-110 transition-all`}
            aria-label="Toggle theme"
          >
            {darkMode ? <Sun className="w-5 h-5 text-yellow-400" /> : <Moon className="w-5 h-5 text-slate-700" />}
          </button>
        </div>

        <div className="max-w-5xl mx-auto px-6 md:px-8 lg:px-12 py-16 space-y-0">
          {/* Cover Section */}
          <section className="text-center space-y-8 py-24 bg-white rounded-lg shadow-sm mb-8 border-b-2 border-gray-100">
            <div className="space-y-4">
              <h1 className="text-6xl md:text-7xl font-bold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">
                b2bcsv.com
              </h1>
              <p className={`text-2xl md:text-3xl ${secondaryTextClass}`}>
                AI-Powered Lead Generation for Modern Businesses
              </p>
            </div>
            <div className={`space-y-2 ${tertiaryTextClass}`}>
              <p className="text-lg font-semibold">Seed Round Presentation</p>
              <p>Austin Sherman, Founder</p>
              <p className="text-sm">{new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</p>
            </div>
          </section>

          {/* The Problem */}
          <section className={`space-y-8 py-20 px-6 md:px-10 rounded-lg mb-8 border-b-2 ${darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-gray-50 border-gray-100'}`}>
            <h2 className="text-4xl md:text-5xl font-bold text-blue-600 text-center">The Problem</h2>
            <p className={`text-xl ${secondaryTextClass} leading-relaxed text-center max-w-3xl mx-auto`}>
              Traditional lead generation tools are failing modern businesses
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className={`${cardBgClass} p-6 rounded-lg border ${borderClass}`}>
                <div className="text-4xl mb-4">💸</div>
                <h3 className="text-xl font-semibold mb-2 text-blue-600">Too Expensive</h3>
                <p className={tertiaryTextClass}>
                  Legacy tools charge hundreds per month for basic features, pricing out small businesses and startups
                </p>
              </div>
              <div className={`${cardBgClass} p-6 rounded-lg border ${borderClass}`}>
                <div className="text-4xl mb-4">🐌</div>
                <h3 className="text-xl font-semibold mb-2 text-blue-600">Too Slow</h3>
                <p className={tertiaryTextClass}>
                  Manual data entry and outdated APIs make lead generation a time-consuming bottleneck
                </p>
              </div>
              <div className={`${cardBgClass} p-6 rounded-lg border ${borderClass}`}>
                <div className="text-4xl mb-4">📊</div>
                <h3 className="text-xl font-semibold mb-2 text-blue-600">Low Quality Data</h3>
                <p className={tertiaryTextClass}>
                  Incomplete contact info, outdated details, and no AI enrichment leave sales teams scrambling
                </p>
              </div>
            </div>
            <div className={`${accentBgClass} p-6 rounded-lg border ${borderClass}`}>
              <p className={`text-lg ${secondaryTextClass}`}>
                <span className="font-semibold text-cyan-600">The result?</span> Sales teams waste hours on manual research instead of closing deals
              </p>
            </div>
          </section>

          {/* The Solution */}
          <section className={`space-y-8 py-20 px-6 md:px-10 rounded-lg mb-8 border-b-2 ${darkMode ? 'bg-slate-900 border-slate-700' : 'bg-white border-gray-100'}`}>
            <h2 className="text-4xl md:text-5xl font-bold text-blue-600 text-center">The Solution</h2>
            <p className={`text-xl ${secondaryTextClass} leading-relaxed text-center max-w-3xl mx-auto`}>
              b2bcsv.com generates enriched, ready-to-use business lead lists in seconds
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center text-white font-bold">
                    1
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2 text-blue-600">Search by Category & Location</h3>
                    <p className={tertiaryTextClass}>
                      Use Google Places API to find businesses matching your ideal customer profile
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center text-white font-bold">
                    2
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2 text-blue-600">AI Enrichment</h3>
                    <p className={tertiaryTextClass}>
                      Gemini AI automatically enriches each business with contact info, industry data, and insights
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center text-white font-bold">
                    3
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2 text-blue-600">Export & Act</h3>
                    <p className={tertiaryTextClass}>
                      Download clean CSV files or export directly to Google Sheets for immediate outreach.
                      <strong className="text-blue-600"> Google Sheets export is fully functional in our MVP</strong>, making it seamless to use with automation tools like Make.com and Zapier for your sales workflows.
                    </p>
                  </div>
                </div>
              </div>
              <div className={`${cardBgClass} p-6 rounded-lg border ${borderClass}`}>
                <h3 className="text-xl font-semibold mb-4 text-cyan-600">Key Advantages</h3>
                <ul className={`space-y-3 ${secondaryTextClass}`}>
                  <li className="flex items-start">
                    <span className="text-cyan-600 mr-2">✓</span>
                    <span><strong>10x faster</strong> than manual research</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-cyan-600 mr-2">✓</span>
                    <span><strong>1/5th the cost</strong> of legacy tools</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-cyan-600 mr-2">✓</span>
                    <span><strong>AI-enriched data</strong> that improves conversion</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-cyan-600 mr-2">✓</span>
                    <span><strong>Google-backed data</strong> means accuracy and freshness</span>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Market Size */}
          <section className={`space-y-8 py-20 px-6 md:px-10 rounded-lg mb-8 border-b-2 ${darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-gray-50 border-gray-100'}`}>
            <h2 className="text-4xl md:text-5xl font-bold text-blue-600 text-center">Market Size</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className={`${cardBgClass} p-8 rounded-lg border ${borderClass} text-center`}>
                <div className="text-4xl font-bold text-blue-600 mb-2">$6.5B</div>
                <div className={`${secondaryTextClass} text-lg font-semibold`}>TAM</div>
                <div className={`${tertiaryTextClass} text-sm mt-2`}>Total Addressable Market</div>
              </div>
              <div className={`${cardBgClass} p-8 rounded-lg border ${borderClass} text-center`}>
                <div className="text-4xl font-bold text-cyan-600 mb-2">$2.1B</div>
                <div className={`${secondaryTextClass} text-lg font-semibold`}>SAM</div>
                <div className={`${tertiaryTextClass} text-sm mt-2`}>Serviceable Addressable Market</div>
              </div>
              <div className={`${cardBgClass} p-8 rounded-lg border ${borderClass} text-center`}>
                <div className="text-4xl font-bold text-blue-600 mb-2">$150M</div>
                <div className={`${secondaryTextClass} text-lg font-semibold`}>SOM</div>
                <div className={`${tertiaryTextClass} text-sm mt-2`}>Serviceable Obtainable Market</div>
              </div>
            </div>
            <div className={`${cardBgClass} p-6 rounded-lg border ${borderClass}`}>
              <h3 className="text-xl font-semibold mb-4 text-cyan-600">Market Segments</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className={secondaryTextClass}>Small Businesses (1-50 employees)</span>
                  <span className="text-blue-600 font-semibold">33M in US alone</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className={secondaryTextClass}>B2B SaaS Companies</span>
                  <span className="text-blue-600 font-semibold">$200B+ industry</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className={secondaryTextClass}>Marketing Agencies</span>
                  <span className="text-blue-600 font-semibold">100K+ agencies</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className={secondaryTextClass}>Sales Operations Teams</span>
                  <span className="text-blue-600 font-semibold">Growing 15% YoY</span>
                </div>
              </div>
            </div>
          </section>

          {/* Business Model */}
          <section className={`space-y-8 py-20 px-6 md:px-10 rounded-lg mb-8 border-b-2 ${darkMode ? 'bg-slate-900 border-slate-700' : 'bg-white border-gray-100'}`}>
            <h2 className="text-4xl md:text-5xl font-bold text-blue-600 text-center">Business Model</h2>
            <p className={`text-xl ${secondaryTextClass} text-center max-w-3xl mx-auto`}>
              Subscription SaaS with usage-based pricing + API revenue
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className={`${cardBgClass} p-6 rounded-lg border ${borderClass}`}>
                <div className="text-center mb-4">
                  <div className={`text-2xl font-bold ${secondaryTextClass}`}>Free</div>
                  <div className="text-cyan-600 text-3xl font-bold my-2">$0</div>
                  <div className={`${tertiaryTextClass} text-sm`}>per month</div>
                </div>
                <ul className={`space-y-2 ${tertiaryTextClass} text-sm`}>
                  <li>• 10 searches/month</li>
                  <li>• 100 exports/month</li>
                  <li>• Basic enrichment</li>
                  <li>• CSV export</li>
                  <li>• Community support</li>
                </ul>
              </div>

              <div className={`${cardBgClass} p-6 rounded-lg border-2 border-blue-500`}>
                <div className="text-center mb-4">
                  <div className={`text-2xl font-bold ${textClass}`}>Starter</div>
                  <div className="text-blue-600 text-3xl font-bold my-2">$24.99</div>
                  <div className={`${tertiaryTextClass} text-sm`}>per month</div>
                </div>
                <ul className={`space-y-2 ${secondaryTextClass} text-sm`}>
                  <li>• <strong>100 searches/month</strong></li>
                  <li>• <strong>1,000 exports/month</strong></li>
                  <li>• Full AI enrichment</li>
                  <li>• CSV & Sheets export</li>
                  <li>• Email support</li>
                </ul>
              </div>

              <div className={`${cardBgClass} p-6 rounded-lg border-2 border-cyan-500`}>
                <div className="text-center mb-4">
                  <div className={`text-2xl font-bold ${textClass}`}>Pro</div>
                  <div className="text-cyan-600 text-3xl font-bold my-2">$59.99</div>
                  <div className={`${tertiaryTextClass} text-sm`}>per month</div>
                </div>
                <ul className={`space-y-2 ${secondaryTextClass} text-sm`}>
                  <li>• <strong>300 searches/month</strong></li>
                  <li>• <strong>3,000 exports/month</strong></li>
                  <li>• Advanced AI enrichment</li>
                  <li>• All export options</li>
                  <li>• Priority support</li>
                  <li>• API access (coming soon)</li>
                </ul>
              </div>

              <div className={`${cardBgClass} p-6 rounded-lg border-2 border-purple-500`}>
                <div className="text-center mb-4">
                  <div className={`text-2xl font-bold ${textClass}`}>Enterprise</div>
                  <div className="text-purple-600 text-3xl font-bold my-2">$299</div>
                  <div className={`${tertiaryTextClass} text-sm`}>per month</div>
                </div>
                <ul className={`space-y-2 ${secondaryTextClass} text-sm`}>
                  <li>• <strong>1,500 searches/month</strong></li>
                  <li>• <strong>15,000 exports/month</strong></li>
                  <li>• <strong>250 enrichments/month</strong></li>
                  <li>• All Pro features</li>
                  <li>• Custom integrations</li>
                  <li>• Dedicated account manager</li>
                  <li>• SLA guarantee</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Traction */}
          <section className={`space-y-8 py-20 px-6 md:px-10 rounded-lg mb-8 border-b-2 ${darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-gray-50 border-gray-100'}`}>
            <h2 className="text-4xl md:text-5xl font-bold text-blue-600 text-center">Traction</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className={`${cardBgClass} p-6 rounded-lg border ${borderClass} text-center`}>
                <div className="text-4xl font-bold text-green-600 mb-2">✓</div>
                <div className={`text-2xl font-bold ${textClass}`}>Live MVP</div>
                <div className={`${tertiaryTextClass} text-sm mt-2`}>In production</div>
              </div>
              <div className={`${cardBgClass} p-6 rounded-lg border ${borderClass} text-center`}>
                <div className="text-4xl font-bold text-cyan-600 mb-2">100%</div>
                <div className={`text-2xl font-bold ${textClass}`}>Uptime</div>
                <div className={`${tertiaryTextClass} text-sm mt-2`}>Reliable & stable</div>
              </div>
              <div className={`${cardBgClass} p-6 rounded-lg border ${borderClass} text-center`}>
                <div className="text-4xl font-bold text-blue-600 mb-2">3</div>
                <div className={`text-2xl font-bold ${textClass}`}>Pricing Tiers</div>
                <div className={`${tertiaryTextClass} text-sm mt-2`}>Active subscriptions</div>
              </div>
              <div className={`${cardBgClass} p-6 rounded-lg border ${borderClass} text-center`}>
                <div className="text-4xl font-bold text-green-600 mb-2">$0</div>
                <div className={`text-2xl font-bold ${textClass}`}>Tech Debt</div>
                <div className={`${tertiaryTextClass} text-sm mt-2`}>Clean codebase</div>
              </div>
            </div>

            <div className={`${accentBgClass} p-8 rounded-lg border ${borderClass}`}>
              <h3 className="text-2xl font-semibold mb-4 text-cyan-600">Product-Market Fit Signals</h3>
              <div className={`space-y-4 ${secondaryTextClass}`}>
                <div className="flex items-start space-x-3">
                  <span className="text-cyan-600 text-xl">→</span>
                  <p><strong>Dogfooding:</strong> Founder actively uses the platform in their own business, proving real-world value</p>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="text-cyan-600 text-xl">→</span>
                  <p><strong>Production-Ready:</strong> Stable infrastructure handling real workloads</p>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="text-cyan-600 text-xl">→</span>
                  <p><strong>Monetization Active:</strong> Paid subscription tiers already in market</p>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="text-cyan-600 text-xl">→</span>
                  <p><strong>Integration Ready:</strong> Google Sheets export working in MVP, enabling seamless workflows with Make.com, Zapier, and other automation platforms</p>
                </div>
              </div>
            </div>
          </section>

          {/* Competitive Landscape */}
          <section className={`space-y-8 py-20 px-6 md:px-10 rounded-lg mb-8 border-b-2 ${darkMode ? 'bg-slate-900 border-slate-700' : 'bg-white border-gray-100'}`}>
            <h2 className="text-4xl md:text-5xl font-bold text-blue-600 text-center">Competitive Landscape</h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className={`border-b-2 ${borderClass}`}>
                    <th className={`text-left p-4 ${textClass}`}>Feature</th>
                    <th className="text-center p-4 text-blue-600">b2bcsv.com</th>
                    <th className={`text-center p-4 ${tertiaryTextClass}`}>Apollo.io</th>
                    <th className={`text-center p-4 ${tertiaryTextClass}`}>ZoomInfo</th>
                    <th className={`text-center p-4 ${tertiaryTextClass}`}>Clearbit</th>
                  </tr>
                </thead>
                <tbody className={secondaryTextClass}>
                  <tr className={`border-b ${borderClass}`}>
                    <td className="p-4">AI Enrichment</td>
                    <td className="text-center p-4 text-green-600">✓ Gemini</td>
                    <td className={`text-center p-4 ${tertiaryTextClass}`}>Limited</td>
                    <td className={`text-center p-4 ${tertiaryTextClass}`}>Manual</td>
                    <td className={`text-center p-4 ${tertiaryTextClass}`}>Basic</td>
                  </tr>
                  <tr className={`border-b ${borderClass}`}>
                    <td className="p-4">Starting Price</td>
                    <td className="text-center p-4 text-green-600">$0-$25</td>
                    <td className="text-center p-4 text-red-600">$49+</td>
                    <td className="text-center p-4 text-red-600">$15k+/yr</td>
                    <td className="text-center p-4 text-red-600">$99+</td>
                  </tr>
                  <tr className={`border-b ${borderClass}`}>
                    <td className="p-4">Setup Time</td>
                    <td className="text-center p-4 text-green-600">&lt;5 min</td>
                    <td className={`text-center p-4 ${tertiaryTextClass}`}>30+ min</td>
                    <td className={`text-center p-4 ${tertiaryTextClass}`}>Days</td>
                    <td className={`text-center p-4 ${tertiaryTextClass}`}>1 hour+</td>
                  </tr>
                  <tr className={`border-b ${borderClass}`}>
                    <td className="p-4">Data Source</td>
                    <td className="text-center p-4 text-green-600">Google Places</td>
                    <td className={`text-center p-4 ${tertiaryTextClass}`}>Proprietary</td>
                    <td className={`text-center p-4 ${tertiaryTextClass}`}>Proprietary</td>
                    <td className={`text-center p-4 ${tertiaryTextClass}`}>Proprietary</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Product Roadmap */}
          <section className={`space-y-8 py-20 px-6 md:px-10 rounded-lg mb-8 border-b-2 ${darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-gray-50 border-gray-100'}`}>
            <h2 className="text-4xl md:text-5xl font-bold text-blue-600 text-center">Product Roadmap</h2>
            <div className="space-y-6">
              <div className={`${cardBgClass} p-6 rounded-lg border-l-4 border-green-500`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-2xl font-semibold text-green-600">Phase 1: Lead Generation (Complete)</h3>
                  <span className="bg-green-500 text-white px-3 py-1 rounded-full text-sm font-semibold">LIVE</span>
                </div>
                <ul className={`space-y-2 ${secondaryTextClass}`}>
                  <li>• Google Places API integration</li>
                  <li>• Gemini AI enrichment</li>
                  <li>• CSV and Google Sheets export (fully functional - integrates with Make.com, Zapier)</li>
                  <li>• User authentication and subscription management</li>
                </ul>
              </div>

              <div className={`${cardBgClass} p-6 rounded-lg border-l-4 border-blue-500`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-2xl font-semibold text-blue-600">Phase 2: Public API (Q1 2026)</h3>
                  <span className="bg-blue-500 text-white px-3 py-1 rounded-full text-sm font-semibold">IN DEV</span>
                </div>
                <ul className={`space-y-2 ${secondaryTextClass}`}>
                  <li>• RESTful API for programmatic access</li>
                  <li>• Developer documentation and SDKs</li>
                  <li>• Usage-based API pricing</li>
                  <li>• Webhook support</li>
                </ul>
              </div>

              <div className={`${cardBgClass} p-6 rounded-lg border-l-4 border-cyan-500`}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-2xl font-semibold text-cyan-600">Phase 3: AI Automations (Q2-Q3 2026)</h3>
                  <span className={`${darkMode ? 'bg-slate-600' : 'bg-gray-400'} text-white px-3 py-1 rounded-full text-sm font-semibold`}>PLANNED</span>
                </div>
                <ul className={`space-y-2 ${secondaryTextClass}`}>
                  <li>• CRM plugins (HubSpot, Salesforce)</li>
                  <li>• Native Zapier and Make.com apps (Google Sheets export already enables these workflows)</li>
                  <li>• AI-powered lead scoring</li>
                  <li>• Automated outreach workflows</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Tech Stack */}
          <section className={`space-y-8 py-20 px-6 md:px-10 rounded-lg mb-8 border-b-2 ${darkMode ? 'bg-slate-900 border-slate-700' : 'bg-white border-gray-100'}`}>
            <h2 className="text-4xl md:text-5xl font-bold text-blue-600 text-center">Tech Stack</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className={`${cardBgClass} p-6 rounded-lg border ${borderClass}`}>
                <h3 className="text-xl font-semibold mb-4 text-cyan-600">Frontend</h3>
                <ul className={`space-y-2 ${secondaryTextClass}`}>
                  <li>• React with TypeScript</li>
                  <li>• Vite for fast builds</li>
                  <li>• Tailwind CSS</li>
                  <li>• React Router</li>
                </ul>
              </div>

              <div className={`${cardBgClass} p-6 rounded-lg border ${borderClass}`}>
                <h3 className="text-xl font-semibold mb-4 text-cyan-600">Backend</h3>
                <ul className={`space-y-2 ${secondaryTextClass}`}>
                  <li>• Node.js + Express</li>
                  <li>• PostgreSQL database</li>
                  <li>• Prisma ORM</li>
                  <li>• Google OAuth</li>
                </ul>
              </div>

              <div className={`${cardBgClass} p-6 rounded-lg border ${borderClass}`}>
                <h3 className="text-xl font-semibold mb-4 text-cyan-600">APIs & AI</h3>
                <ul className={`space-y-2 ${secondaryTextClass}`}>
                  <li>• Google Places API</li>
                  <li>• Gemini AI</li>
                  <li>• Google Sheets API</li>
                  <li>• Stripe for payments</li>
                </ul>
              </div>

              <div className={`${cardBgClass} p-6 rounded-lg border ${borderClass}`}>
                <h3 className="text-xl font-semibold mb-4 text-cyan-600">Infrastructure</h3>
                <ul className={`space-y-2 ${secondaryTextClass}`}>
                  <li>• Railway hosting</li>
                  <li>• PostgreSQL managed DB</li>
                  <li>• Git version control</li>
                  <li>• CI/CD ready</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Why Now */}
          <section className={`space-y-8 py-20 px-6 md:px-10 rounded-lg mb-8 border-b-2 ${darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-gray-50 border-gray-100'}`}>
            <h2 className="text-4xl md:text-5xl font-bold text-blue-600 text-center">Why Now</h2>

            <div className={`${accentBgClass} p-8 rounded-lg border-2 border-blue-500`}>
              <p className={`text-xl ${textClass} font-semibold text-center mb-6`}>
                B2B CSV is not just a good idea – it's an idea whose time has come.
              </p>
              <p className={`${secondaryTextClass} text-center leading-relaxed`}>
                We're at the intersection of a massive market need and a technological breakthrough that makes our solution possible today.
                Five years ago, the AI tech wasn't ready. Five years from now, the SMB segment might be crowded.
                <strong className="text-blue-600"> But right now, we have a window to become the go-to tool for cost-effective, smart lead generation.</strong>
              </p>
            </div>

            <div className="space-y-6">
              <div className={`${accentBgClass} p-8 rounded-lg border ${borderClass}`}>
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 text-4xl">🤖</div>
                  <div>
                    <h3 className="text-2xl font-semibold mb-3 text-blue-600">AI Has Reached Critical Mass</h3>
                    <p className={`${secondaryTextClass} leading-relaxed mb-3`}>
                      In 2024-2025, AI models and APIs have reached a level of maturity and affordability that makes AI-driven data products
                      feasible for solo founders. This democratization of AI is a new wave – and we're riding it.
                    </p>
                    <p className={`${secondaryTextClass} leading-relaxed`}>
                      <strong className="text-blue-600">What changed:</strong> Gemini AI can enrich business data for pennies per record vs. dollars with legacy providers.
                      Even incumbents like ZoomInfo are scrambling to add AI features (Copilot in 2023), validating the industry shift we're capitalizing on.
                    </p>
                  </div>
                </div>
              </div>

              <div className={`${accentBgClass} p-8 rounded-lg border ${borderClass}`}>
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 text-4xl">📈</div>
                  <div>
                    <h3 className="text-2xl font-semibold mb-3 text-cyan-600">Market Need Is Acute Right Now</h3>
                    <p className={`${secondaryTextClass} leading-relaxed mb-3`}>
                      Post-2020, remote work and digital sales processes have exploded. Small businesses must embrace online prospecting,
                      but traditional methods don't cut it for scrappy SMBs.
                    </p>
                    <div className={`${cardBgClass} p-4 rounded-lg border ${borderClass} mt-4`}>
                      <p className={`${secondaryTextClass} mb-2`}><strong className="text-cyan-600">Current market reality:</strong></p>
                      <ul className={`${secondaryTextClass} space-y-1 ml-4`}>
                        <li>• 69% of B2B companies plan to increase lead gen investment in 2025</li>
                        <li>• 91% of marketers say lead gen is their top priority</li>
                        <li>• 61% cite it as their biggest challenge</li>
                      </ul>
                      <p className={`${textClass} mt-3 font-semibold`}>The hunger for solutions is immediate – not theoretical.</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className={`${accentBgClass} p-8 rounded-lg border ${borderClass}`}>
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 text-4xl">🎯</div>
                  <div>
                    <h3 className="text-2xl font-semibold mb-3 text-blue-600">Incumbents Left a Gap</h3>
                    <p className={`${secondaryTextClass} leading-relaxed mb-3`}>
                      Big data platforms (ZoomInfo, Apollo) are expensive and enterprise-focused, locking customers into long contracts and high fees.
                      Small businesses either struggle with outdated methods or settle for tools that strain their budget.
                    </p>
                    <p className={`${secondaryTextClass} leading-relaxed`}>
                      <strong className="text-blue-600">Our advantage:</strong> We provide 80% of ZoomInfo's value at 10% of the cost.
                      Our $25-$60/month pricing is a game-changer for the long tail of customers priced out of enterprise tools.
                    </p>
                  </div>
                </div>
              </div>

              <div className={`${accentBgClass} p-8 rounded-lg border ${borderClass}`}>
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 text-4xl">⚡</div>
                  <div>
                    <h3 className="text-2xl font-semibold mb-3 text-cyan-600">Data & Infrastructure Are Ready</h3>
                    <p className={`${secondaryTextClass} leading-relaxed mb-3`}>
                      Public data sources (listings, social media, government databases) are more accessible via APIs than ever.
                      Cloud infrastructure makes it possible to build powerful tools without massive capital.
                    </p>
                    <p className={`${secondaryTextClass} leading-relaxed`}>
                      In the era of GDPR/CCPA, businesses want fresh, targeted, compliant data – not random lists.
                      Our AI-curated approach using public data aligns perfectly with 2025's demand for quality over quantity.
                    </p>
                  </div>
                </div>
              </div>

              <div className={`${accentBgClass} p-8 rounded-lg border ${borderClass}`}>
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 text-4xl">💡</div>
                  <div>
                    <h3 className="text-2xl font-semibold mb-3 text-blue-600">Founder's Unique Timing</h3>
                    <p className={`${secondaryTextClass} leading-relaxed mb-3`}>
                      Built in 2025 when all the pieces finally came together: AI APIs, cloud infrastructure, and proven market need.
                      Not just theory – I experienced the problem firsthand and validated the solution in my own business.
                    </p>
                    <p className={`${secondaryTextClass} leading-relaxed`}>
                      This "insider advantage" means we understand the pain point deeply and have proven product-market fit before even raising capital.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className={`${cardBgClass} p-8 rounded-lg border-2 border-blue-500 text-center`}>
              <p className={`text-2xl ${textClass} font-semibold mb-4`}>
                Why hasn't this been done before?
              </p>
              <p className={`text-lg ${secondaryTextClass} leading-relaxed`}>
                It <strong className="text-blue-600">couldn't</strong> have been done – GPT-4 and large-scale APIs weren't available,
                and small businesses weren't as online-focused.
              </p>
              <p className={`text-lg ${secondaryTextClass} leading-relaxed mt-4`}>
                It <strong className="text-cyan-600">must</strong> be done now – AI is commoditized, everyone needs more leads post-2020,
                and we have a narrow window before others catch up.
              </p>
              <p className={`text-xl ${textClass} font-bold mt-6`}>
                We're raising this seed round to seize this moment and scale fast.
              </p>
            </div>
          </section>

          {/* Team */}
          <section className={`space-y-8 py-20 px-6 md:px-10 rounded-lg mb-8 border-b-2 ${darkMode ? 'bg-slate-900 border-slate-700' : 'bg-white border-gray-100'}`}>
            <h2 className="text-4xl md:text-5xl font-bold text-blue-600 text-center">Team</h2>
            <div className={`${accentBgClass} p-8 rounded-lg border ${borderClass}`}>
              <div className="flex flex-col md:flex-row items-start gap-6">
                <div className="flex-shrink-0">
                  <div className="w-32 h-32 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center text-6xl text-white font-bold">
                    AS
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-3xl font-bold text-blue-600 mb-2">Austin Sherman</h3>
                  <p className="text-xl text-cyan-600 mb-4">Founder & Solo Developer</p>
                  <div className={`space-y-3 ${secondaryTextClass}`}>
                    <p>
                      <strong className="text-blue-600">Background:</strong> Full-stack developer with hands-on B2B sales experience
                    </p>
                    <p>
                      <strong className="text-blue-600">Unique Insight:</strong> Built b2bcsv.com to solve own business pain points
                    </p>
                    <p>
                      <strong className="text-blue-600">Technical:</strong> React, Node.js, PostgreSQL, AI integration - full product development
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className={`${cardBgClass} p-6 rounded-lg border ${borderClass}`}>
                <h3 className="text-xl font-semibold mb-4 text-cyan-600">Current Status</h3>
                <ul className={`space-y-2 ${secondaryTextClass}`}>
                  <li>• Sole proprietorship</li>
                  <li>• 100% solo-built MVP</li>
                  <li>• Zero technical debt</li>
                  <li>• Ready to scale</li>
                </ul>
              </div>

              <div className={`${cardBgClass} p-6 rounded-lg border ${borderClass}`}>
                <h3 className="text-xl font-semibold mb-4 text-cyan-600">Post-Funding</h3>
                <ul className={`space-y-2 ${secondaryTextClass}`}>
                  <li>• Convert to Delaware C-Corp</li>
                  <li>• Hire remote developer (part-time)</li>
                  <li>• Technical/security/business advisor</li>
                  <li>• Create advisory board</li>
                  <li>• Customer success team & chatbot</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Financial Projections */}
          <section className={`space-y-8 py-20 px-6 md:px-10 rounded-lg mb-8 border-b-2 ${darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-gray-50 border-gray-100'}`}>
            <h2 className="text-4xl md:text-5xl font-bold text-blue-600 text-center">Financial Projections</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className={`${cardBgClass} p-6 rounded-lg border ${borderClass} text-center`}>
                <div className="text-3xl font-bold text-cyan-600 mb-2">$7-10k</div>
                <div className={`${secondaryTextClass} font-semibold`}>MRR Target</div>
                <div className={`${tertiaryTextClass} text-sm`}>Month 12</div>
              </div>
              <div className={`${cardBgClass} p-6 rounded-lg border ${borderClass} text-center`}>
                <div className="text-3xl font-bold text-blue-600 mb-2">&lt;$100</div>
                <div className={`${secondaryTextClass} font-semibold`}>CAC</div>
                <div className={`${tertiaryTextClass} text-sm`}>Target</div>
              </div>
              <div className={`${cardBgClass} p-6 rounded-lg border ${borderClass} text-center`}>
                <div className="text-3xl font-bold text-cyan-600 mb-2">8-12%</div>
                <div className={`${secondaryTextClass} font-semibibold`}>Conversion</div>
                <div className={`${tertiaryTextClass} text-sm`}>Free to Paid</div>
              </div>
              <div className={`${cardBgClass} p-6 rounded-lg border ${borderClass} text-center`}>
                <div className="text-3xl font-bold text-blue-600 mb-2">~$10k/mo</div>
                <div className={`${secondaryTextClass} font-semibold`}>Burn Rate</div>
                <div className={`${tertiaryTextClass} text-sm`}>Lean ops</div>
              </div>
            </div>
          </section>

          {/* The Ask */}
          <section className={`space-y-8 py-20 px-6 md:px-10 rounded-lg mb-8 border-b-2 ${darkMode ? 'bg-slate-900 border-slate-700' : 'bg-white border-gray-100'}`}>
            <h2 className="text-4xl md:text-5xl font-bold text-blue-600 text-center">The Ask</h2>
            <div className={`${accentBgClass} p-12 rounded-lg border-2 border-blue-500 text-center`}>
              <h3 className="text-4xl font-bold text-cyan-600 mb-6">Raising $250k</h3>
              <p className={`text-2xl ${secondaryTextClass} mb-2`}>YC Post-Money SAFE</p>
              <p className="text-3xl font-bold text-blue-600">$2M Valuation Cap</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className={`${cardBgClass} p-6 rounded-lg border ${borderClass}`}>
                <h3 className="text-xl font-semibold mb-4 text-cyan-600">Use of Funds</h3>
                <div className={`space-y-3 ${secondaryTextClass}`}>
                  <div className="flex justify-between">
                    <span>API Development</span>
                    <span className="text-blue-600 font-semibold">25%</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Marketing & Growth</span>
                    <span className="text-blue-600 font-semibold">25%</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Operations & Salary</span>
                    <span className="text-blue-600 font-semibold">25%</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Feature Dev & Crypto</span>
                    <span className="text-blue-600 font-semibold">10%</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Legal & C-Corp</span>
                    <span className="text-blue-600 font-semibold">10%</span>
                  </div>
                  <div className="flex justify-between">
                    <span>UI/UX Enhancement</span>
                    <span className="text-blue-600 font-semibold">5%</span>
                  </div>
                </div>
              </div>

              <div className={`${cardBgClass} p-6 rounded-lg border ${borderClass}`}>
                <h3 className="text-xl font-semibold mb-4 text-cyan-600">SAFE Terms</h3>
                <ul className={`space-y-3 ${secondaryTextClass}`}>
                  <li>• <strong className="text-blue-600">Cap:</strong> $2M</li>
                  <li>• <strong className="text-blue-600">Discount:</strong> None</li>
                  <li>• <strong className="text-blue-600">Board Seat:</strong> No</li>
                  <li>• <strong className="text-blue-600">Terms:</strong> Standard YC</li>
                </ul>
              </div>

              <div className={`${cardBgClass} p-6 rounded-lg border ${borderClass}`}>
                <h3 className="text-xl font-semibold mb-4 text-cyan-600">What You Get</h3>
                <ul className={`space-y-3 ${secondaryTextClass}`}>
                  <li>• Working, production-ready MVP</li>
                  <li>• Zero technical debt</li>
                  <li>• Proven product-market fit</li>
                  <li>• Founder fully dedicated to growth</li>
                  <li>• Clear path to profitability</li>
                  <li>• Large addressable market</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Contact */}
          <section className={`space-y-8 py-20 px-6 md:px-10 rounded-lg border-b-2 ${darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-gray-50 border-gray-100'}`}>
            <h2 className="text-4xl md:text-5xl font-bold text-blue-600 text-center">Let's Build Together</h2>
            <div className={`${accentBgClass} p-8 rounded-lg border ${borderClass} text-center`}>
              <h3 className="text-2xl font-semibold text-cyan-600 mb-6">Austin Sherman</h3>
              <div className={`space-y-3 ${secondaryTextClass}`}>
                <p className="flex items-center justify-center space-x-3">
                  <span className="text-blue-600">🌐</span>
                  <a href="https://b2bcsv.com" target="_blank" rel="noopener noreferrer" className="hover:text-blue-600 transition-colors">
                    b2bcsv.com
                  </a>
                </p>
              </div>
            </div>

            <div className={`${cardBgClass} p-8 rounded-lg border-2 border-blue-500 text-center`}>
              <p className={`text-2xl ${textClass} font-semibold`}>
                18-24 month runway to Series A metrics
              </p>
            </div>
          </section>
        </div>
      </div>
    </Layout>
  );
}
